import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { PillBottle, ArrowRight, Loader2 } from "lucide-react";
import GoogleButton from "@/components/GoogleButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success("Connexion réussie");
      navigate(user.role === "admin" ? "/admin" : user.role === "supplier" ? "/supplier" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Échec de connexion");
    } finally {
      setLoading(false);
    }
  };

  const demo = [
    ["Pharmacien", "pharmacien@pharmab2b.tn", "Pharma@2026"],
    ["Fournisseur", "contact@svr.tn", "Supplier@2026"],
    ["Admin", "admin@pharmab2b.tn", "Admin@2026"],
  ];

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0F172A] text-white p-12">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-lg bg-indigo-600 grid place-items-center"><PillBottle className="h-5 w-5" /></div>
          <span className="font-extrabold">PharmaGroup</span>
        </Link>
        <div>
          <h2 className="text-4xl font-extrabold tracking-tight leading-tight">Achats groupés.<br />Meilleures conditions.</h2>
          <p className="mt-4 text-slate-400 font-medium max-w-sm">La plateforme B2B qui connecte pharmaciens et fournisseurs tunisiens.</p>
        </div>
        <div className="text-sm text-slate-500">© 2026 PharmaGroup B2B</div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div className="h-9 w-9 rounded-lg bg-indigo-600 grid place-items-center"><PillBottle className="h-5 w-5 text-white" /></div>
            <span className="font-extrabold text-slate-900">PharmaGroup</span>
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900">Connexion</h1>
          <p className="text-slate-500 font-medium mt-1">Accédez à votre espace professionnel.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-semibold text-slate-700">Email</label>
              <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow" placeholder="vous@pharmacie.tn" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-sm font-semibold text-slate-700">Mot de passe</label>
                <Link to="/forgot-password" data-testid="forgot-password-link" className="text-xs font-semibold text-indigo-600 hover:underline">Mot de passe oublié ?</Link>
              </div>
              <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 w-full h-11 px-3.5 rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-indigo-600 transition-shadow" placeholder="••••••••" />
            </div>
            {error && <div data-testid="login-error" className="text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <button data-testid="login-submit" disabled={loading} className="w-full h-11 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-semibold flex items-center justify-center gap-2 transition-colors duration-200 disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Se connecter <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>

          <div className="my-5 flex items-center gap-3 text-xs font-semibold text-slate-400"><div className="h-px bg-slate-200 flex-1" />OU<div className="h-px bg-slate-200 flex-1" /></div>
          <GoogleButton />

          <p className="mt-6 text-sm text-slate-500">Pas encore de compte ? <Link to="/register" className="font-semibold text-indigo-600 hover:underline">Inscrivez-vous</Link></p>

          <div className="mt-8 p-4 rounded-xl bg-white border border-slate-200">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-wide mb-2">Comptes de démonstration</div>
            <div className="space-y-1.5">
              {demo.map(([role, e, p]) => (
                <button key={e} onClick={() => { setEmail(e); setPassword(p); }} data-testid={`demo-${role.toLowerCase()}`}
                  className="w-full text-left text-xs flex justify-between gap-2 px-2.5 py-1.5 rounded-md hover:bg-slate-50 transition-colors duration-200">
                  <span className="font-semibold text-slate-700">{role}</span>
                  <span className="text-slate-400 num">{e}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
